import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Tag } from 'antd';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useGetItems } from 'src/reactQuery/items/useGetItems';
import { isCurrentProjectBuildy, isCurrentProjectStimull } from 'src/util/projectCheckers';
import { getCurrentUser, getUserRoleIsUser } from 'src/util/isCurrentUserRoot';
import { useQueryParams } from 'src/hooks/useQueryParams';
import { downloadOrdersCsv } from 'src/services/napi/orders/downloadOrdersCsv';
import { useGetOrders } from 'src/reactQuery/orders/useGetOrders';
import { downloadFileAction } from '../../actions/ordersAction';
import NoxTable from '../../component/Table';
import InnerHeader from '../../component/InnerHeader';
import Loader from '../../component/Loader/Loader';
import Copy from '../../component/Copy';

const Orders = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  const history = useHistory();
  const dispatch = useDispatch();

  const { queryParams, setQueryParams } = useQueryParams();

  const currentProject = useSelector(({ project }) => project.currentProject);
  const { data: itemsData } = useGetItems(queryParams.page || 1, queryParams.pageSize || 10);
  const { data: orders, isLoading } = useGetOrders({
    itemId: queryParams.items || '',
    page: queryParams.page || 1,
    pageSize: queryParams.pageSize || 10,
    search: queryParams.search || ''
  });

  const openDetail = (id) => {
    history.push({
      pathname: `/ecommerce/orders/${id}`
    });
  };

  useEffect(() => {
    const fetchUser = async () => {
      const user = await getCurrentUser();
      setCurrentUser(user);
    };

    fetchUser();
  }, []);

  useEffect(() => {
    if (!queryParams.filter) {
      setQueryParams({ ...queryParams, filter: 'All' });
    }
    if (!queryParams.items) {
      setQueryParams({ ...queryParams, items: 'All' });
    }
  }, [currentProject, dispatch, queryParams]);

  const onDownloadCsv = () => {
    if (isCurrentProjectStimull) {
      const selectedItemId = itemsData
        ?.filter((item) => item.title !== null)
        .filter((item) => item._id === queryParams.items)[0]?._id;

      downloadOrdersCsv({
        itemId: selectedItemId,
        itemName: queryParams.items,
        projectId: currentProject._id
      });
      return;
    }
    dispatch(downloadFileAction());
  };

  const mapToTable = (orders) => {
    const data = [];
    let filteredArray = orders;

    if (!orders || orders.length === 0) {
      return data;
    }

    if (isCurrentProjectBuildy && getUserRoleIsUser()) {
      filteredArray = orders.filter((order) => {
        return order.sellerId === currentUser.tags.seller;
      });
    }

    filteredArray.forEach(
      (
        {
          _id,
          sellerProfile,
          customerProfile,
          dropoffAddress,
          selectedItems,
          deliveryFee,
          status,
          groupId,
          totalPrice
        },
        index
      ) => {
        const total = parseFloat(groupId ? '0' : deliveryFee) + parseFloat(totalPrice);
        const selectedItemId = selectedItems[0].itemId;
        const selectedItemName = selectedItems[0]?.item?.title;
        data.push({
          buyer: customerProfile?.userName,
          id: _id,
          items: selectedItems.length,
          key: index,
          location: dropoffAddress?.street || '',
          selectedItemId,
          selectedItemName,
          seller: sellerProfile?.userName,
          status,
          total: Number.isNaN(total) ? 0 : total.toFixed(2)
        });
      }
    );

    return data;
  };

  const filterData = useCallback(
    (data) => {
      if (!data || !data.length) {
        return;
      }

      if (queryParams.filter !== 'All' && queryParams.items !== 'All') {
        return data.filter((order) => {
          return order.selectedItemId === queryParams.items && order.status === queryParams.filter;
        });
      }

      if (queryParams.filter !== 'All') {
        return data.filter((order) => order.status === queryParams.filter);
      }

      if (queryParams.items !== 'All') {
        return data.filter((order) => order.selectedItemId === queryParams.items);
      }

      return data;
    },
    [queryParams.filter, queryParams.items]
  );

  const columns = [
    {
      dataIndex: 'id',
      key: 'id',
      render: (id) => {
        return <Copy text={id} />;
      },
      sorter: (a, b) => a.id.localeCompare(b.id),
      title: 'Order ID'
    },
    {
      dataIndex: 'seller',
      key: 'seller',
      render: (seller) => {
        return <Copy text={seller} />;
      },
      sorter: (a, b) => a.seller.toString().localeCompare(b.seller),
      title: 'Seller'
    },
    {
      dataIndex: 'buyer',
      key: 'buyer',
      render: (buyer) => {
        return <Copy text={buyer} />;
      },
      sorter: (a, b) => a.buyer.toString().localeCompare(b.buyer),
      title: 'Buyer'
    },
    {
      dataIndex: 'location',
      key: 'location',
      render: (location) => {
        return <Copy text={location} />;
      },
      sorter: (a, b) => a.location.toString().localeCompare(b.location),
      title: 'Delivery Location'
    },
    {
      dataIndex: 'items',
      key: 'items',
      render: (items) => {
        return <Copy text={items} />;
      },
      sorter: (a, b) => a.items.toString().localeCompare(b.items),
      title: 'Item(s)'
    },
    {
      dataIndex: 'total',
      key: 'total',
      render: (total) => {
        return <Copy text={total} />;
      },
      sorter: (a, b) => a.total - b.total,
      title: 'total'
    },
    {
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let style = {};
        switch (status) {
          case 'delivered':
            style = {
              backgroundColor: 'rgba(65, 204, 156, 0.15)',
              border: 'none',
              color: '#41CC9C',
              width: '90%'
            };
            break;
          case 'pending':
            style = {
              backgroundColor: 'rgba(155, 160, 173, 0.15)',
              border: 'none',
              color: '#9BA0AD',
              width: '90%'
            };
            break;
          case 'cancelled':
            style = {
              backgroundColor: 'rgba(255, 71, 108, 0.15)',
              border: 'none',
              color: '#FF476C',
              width: '90%'
            };
            break;
          case 'accepted':
            style = {
              backgroundColor: 'rgba(244, 211, 37, 0.15)',
              border: 'none',
              color: '#F4CD00',
              width: '90%'
            };
            break;
          default:
            style = {
              width: '90%'
            };
        }
        return (
          <Tag key={status} style={style}>
            {status}
          </Tag>
        );
      },
      sorter: (a, b) => a.status.localeCompare(b.status),
      title: 'Status'
    },
    {
      key: 'edit',
      render: (order) => {
        return <Tag onClick={() => openDetail(order.id)}>View</Tag>;
      }
    }
  ];

  const actions = [
    {
      onClick: onDownloadCsv,
      title: 'Download .CSV',
      type: 'primary'
    }
  ];

  const filterList = [
    'All',
    'pending',
    'accepted',
    'processing',
    'onHold',
    'cancelled',
    'delivering',
    'delivered',
    'completed',
    'refunded',
    'failed',
    'trash'
  ];

  const filterListItem = useMemo(() => {
    if (!itemsData) {
      return ['All'];
    }

    return [
      'All',
      ...new Set(itemsData?.filter((item) => item.title !== null).map((item) => item.title))
    ];
  }, [itemsData]);

  const onFilterChange = (value, index) => {
    let id = '';
    if (index === 1) {
      id = itemsData
        ?.filter((item) => item.title !== null)
        .filter((item) => item.title === value)[0]?._id;
    }
    if (index === 0) {
      setQueryParams({ ...queryParams, filter: value });
    } else if (index === 1) {
      setQueryParams({ ...queryParams, items: id });
    }
  };

  const data = mapToTable(orders?.data);

  const filteredData = filterData(data);

  if (!currentProject || isLoading) {
    return <Loader />;
  }

  return (
    <div
      style={{
        height: 'calc(100vh - 165px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={orders?.paging?.total || 0} title="Orders" />
      <NoxTable
        actions={actions}
        columns={
          !isCurrentProjectStimull
            ? columns
            : columns.filter((column) => column.dataIndex !== 'location')
        }
        data={filteredData}
        // onRowClick={openDetail}
        filterList={[filterList, filterListItem]}
        onFilterChange={onFilterChange}
        searchBy="seller"
        selectedFilter={[
          queryParams.filter || 'All',
          itemsData?.find((filterItem) => filterItem._id === queryParams.items)?.title || 'All'
        ]}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        totalPages={orders?.paging?.total || 1}
      />
    </div>
  );
};

export default Orders;
